import ConditionalLink from '@plone/volto/components/manage/ConditionalLink/ConditionalLink';
import cx from 'classnames';
import { defineMessages, type IntlShape, useIntl } from 'react-intl';

const messages = defineMessages({
  ButtonText: {
    id: 'Button text',
    defaultMessage: 'Button text',
  },
});

type ButtonVariant =
  | 'secondary'
  | 'primary'
  | 'tertiary'
  | 'magic'
  | 'danger'
  | 'success'
  | 'warning';
type ButtonSize = 'default' | 'small' | 'large';

export interface ButtonProps
  extends React.ButtonHTMLAttributes<HTMLButtonElement> {
  label?: string;
  link?: string;
  variant?: ButtonVariant;
  size?: ButtonSize;
  isCircle?: boolean;
  inverted?: boolean;
  icon?: string;
  isEditMode?: boolean;
  className?: string;
  showPlaceholder?: boolean;
  active?: boolean;
  children?: React.ReactNode;
}

type CircleAriaProps = {
  'aria-label'?: string;
};

type InteractiveButtonProps = {
  className: string;
  ariaProps: CircleAriaProps;
  shouldRenderLink: boolean;
  link?: string;
  children: React.ReactNode;
} & Omit<
  React.ButtonHTMLAttributes<HTMLButtonElement>,
  'className' | 'children' | 'aria-label'
>;

const InteractiveButton: React.FC<InteractiveButtonProps> = ({
  className,
  ariaProps,
  shouldRenderLink,
  link,
  children,
  ...buttonProps
}) => {
  if (shouldRenderLink && link) {
    return (
      <ConditionalLink href={link} className={className} {...ariaProps}>
        {children}
      </ConditionalLink>
    );
  }

  return (
    <button className={className} {...ariaProps} {...buttonProps}>
      {children}
    </button>
  );
};

type PlaceholderButtonProps = {
  className: string;
  ariaProps: CircleAriaProps;
  children: React.ReactNode;
};

const PlaceholderButton: React.FC<PlaceholderButtonProps> = ({
  className,
  ariaProps,
  children,
}) => (
  <span className={className} {...ariaProps}>
    {children}
  </span>
);

type DefaultContentProps = {
  icon?: string;
  iconClassName: string;
  isCircle?: boolean;
  label?: string;
  intl: IntlShape;
};

const DefaultContent: React.FC<DefaultContentProps> = ({
  icon,
  iconClassName,
  isCircle,
  label,
  intl,
}) => (
  <>
    {(icon || isCircle) && <i className={iconClassName} aria-hidden="true" />}
    {!isCircle && (label || intl.formatMessage(messages.ButtonText))}
  </>
);

const Button: React.FC<ButtonProps> = ({
  label,
  link,
  variant = 'secondary',
  size = 'default',
  isCircle,
  inverted,
  icon,
  isEditMode,
  className,
  showPlaceholder,
  children,
  active = false,
  ...rest
}) => {
  const intl = useIntl();

  const isMagicVariant = variant === 'magic';
  const shouldRenderLink = !!link && !isEditMode;

  const { 'aria-label': ariaLabelFromProps, ...buttonProps } = rest;
  const ariaLabel =
    ariaLabelFromProps ?? (isCircle && label ? label : undefined);
  const ariaProps: CircleAriaProps = ariaLabel
    ? { 'aria-label': ariaLabel }
    : {};

  const buttonClassName = cx(
    'item br-button auto-sm',
    variant && variant !== 'tertiary' && variant,
    !isMagicVariant && className,
    size && size !== 'default' && size,
    {
      'width-100': !isCircle,
      'dark-mode': inverted,
      circle: isCircle,
      active,
      'border-dashed-sm opacity-5': showPlaceholder,
    },
  );

  const magicWrapperClassName = cx(
    'item br-magic-button',
    className,
    size && size !== 'default' && size,
    { 'width-100': !isCircle },
  );

  const iconClassName = cx(icon || 'fa-regular fa-calendar', {
    'mr-2': !isCircle,
  });

  const shouldShowPlaceholder = showPlaceholder && !shouldRenderLink;

  const content = children ?? (
    <DefaultContent
      icon={icon}
      iconClassName={iconClassName}
      isCircle={isCircle}
      label={label}
      intl={intl}
    />
  );

  const buttonElement = shouldShowPlaceholder ? (
    <PlaceholderButton className={buttonClassName} ariaProps={ariaProps}>
      {content}
    </PlaceholderButton>
  ) : (
    <InteractiveButton
      className={buttonClassName}
      ariaProps={ariaProps}
      shouldRenderLink={shouldRenderLink}
      link={link}
      {...buttonProps}
    >
      {content}
    </InteractiveButton>
  );

  return isMagicVariant ? (
    <div className={magicWrapperClassName}>{buttonElement}</div>
  ) : (
    buttonElement
  );
};

export default Button;
